/** Antworten der API (server/app/schemas), nur die Felder, die die Web-App nutzt. */

export type Status = "eingang" | "verarbeitung" | "pruefen" | "archiviert" | "fehler" | "getrennt";

/** `nutzung_hinweis_offen` (W77, D-292): nur im eigenen Nutzer von `ich`; das Konto bekommt einmal das Blatt zur
 *  Nutzungsstatistik. Fehlt bei einem Server vor W77. */
export type Nutzer = {
  id: string;
  email: string;
  name: string;
  rolle: "inhaber" | "mitglied";
  erinnerung_tage: number;
  nutzung_aus?: boolean;
  nutzung_hinweis_offen?: boolean;
};
export type Haushalt = { id: string; name: string; plan: string };
/** `eingefuehrt`: Einfuehrung nach der Registrierung gesehen (W9). */
/** `bestaetigt`: Mailadresse ueber den Link bestaetigt (W9); vorher verschickt die App nichts. */
export type Ich = {
  nutzer: Nutzer;
  haushalt: Haushalt;
  csrf: string;
  eingefuehrt?: boolean;
  bestaetigt?: boolean;
  betreiber?: boolean;
  /** W82 Beta-W (Server Beta-S, Vertrag Abschnitt 10): der Haushalt ist in der Beta (Beta-Kennung gesetzt); fehlt bei Servern
   *  vor Beta-S, dann gilt nein (kein Zeichen „Beta“, keine Begrüßung). */
  beta?: boolean;
  einwilligung_fassung?: string | null;
  /** W20 (D-063): das Konto hat ein Passwort; sonst nur Anmeldung per Link. Fehlt es (Server vor
   *  W20), gilt wie beim Server: ja. */
  hat_passwort?: boolean;
  /** W20b: bis wann diese Sitzung als frischer Nachweis des Postfachs gilt (per Link begonnen, zehn
   *  Minuten); null: keiner. Damit legt ein Konto ohne Passwort eins fest, loescht, uebergibt und signiert. */
  nachweis_frisch_bis: string | null;
  /** W20b: „Passwort festlegen“ geht gerade (kein Passwort und frischer Nachweis). */
  passwort_setzen_moeglich: boolean;
  /** W68: verbundene Anbieter (sortiert); fehlt bei einem Server vor W68. */
  anbieter?: ("apple" | "google")[];
  /** W68: die Kontoadresse ist die Weiterleitungsadresse einer Apple-Verbindung. */
  email_privat?: boolean;
  /** W68: welcher Anbieter mit dieser Antwort gerade verbunden wurde (Link aus der Mail). */
  soeben_verbunden?: "apple" | "google" | null;
  /** W81 (D-331, Server W81): das Freigeben einzelner Briefe ist eingeschaltet (`SP_BRIEF_FREIGABE`); fehlt bei Servern
   *  vor W81. */
  brief_freigabe?: boolean;
  /** W81: Zahl der eigenen Freigaben (auch bei ausgeschaltetem Freigeben); fehlt bei Servern vor W81. */
  freigaben?: number;
  /** W81 (Nachtrag D-433): Wahl des Kontos, Standard `einzeln`; fehlt bei Servern ohne den Nachtrag. */
  brief_freigabe_wahl?: FreigabeWahl;
  /** W81 B5 (Server A2): das Konto hatte `alle` gewaehlt, aber in eine aeltere Fassung der Einwilligung eingewilligt; die
   *  Wahl steht dann schon auf `einzeln`. Fehlt bei Servern ohne den Nachtrag. */
  brief_freigabe_neu_einwilligen?: boolean;
};

/** W81 (Nachtrag D-433): alle Briefe beim Ablegen freigeben, je Brief entscheiden oder nie fragen. */
export type FreigabeWahl = "alle" | "einzeln" | "nie";

/** W68: eingerichtete Anmeldewege (`GET /api/auth/anbieter`); ohne Einstellungen alles false. */
export type AnbieterStand = {
  apple_web: boolean;
  apple_ios: boolean;
  google_web: boolean;
  google_ios: boolean;
};

/** W68: Antwort von `POST /api/auth/anbieter/start`; im Web `url` gesetzt, `nonce` und `rueckweg_schema` null. */
export type AnbieterStartAntwort = {
  state: string;
  url: string | null;
  nonce: string | null;
  rueckweg_schema: string | null;
};

/** W68: Antwort von `POST /api/auth/anbieter/einloesen`. `ich` bei angemeldet, verbunden und bestaetigt; `mail` bei
 *  mail_gesendet; `ticket`, `email` und `email_privat` bei registrierung. */
export type AnbieterErgebnis = {
  ergebnis: "angemeldet" | "verbunden" | "bestaetigt" | "mail_gesendet" | "registrierung";
  ich: Ich | null;
  mail: "verbinden" | "bestaetigung" | null;
  ticket: string | null;
  email: string | null;
  email_privat: boolean;
};

/** W15: Nutzungsbericht fuer den Betreiber (nur Zaehlwerte ueber alle Konten). */
export type Nutzungsbericht = {
  zeitraum_tage: number;
  ereignisse: number;
  nutzer: number;
  aktiv: { heute: number; "7_tage": number; "30_tage": number };
  sitzungen: { anzahl: number; je_nutzer: number; dauer_median_ms: number };
  bildschirme: { plattform: string; name: string; aufrufe: number; nutzer: number; verweildauer_median_ms: number; verweildauer_summe_ms: number }[];
  aktionen: { plattform: string; name: string; anzahl: number; nutzer: number }[];
  nie_genutzt: { plattform: string; name: string }[];
  wiederkehr: Record<"tag_1" | "tag_7" | "tag_30", { basis: number; zurueck: number }>;
  plattformen: Record<string, number>;
  versionen: { plattform: string; version: string; sitzungen: number }[];
  geraete: { geraet: string; os: string; sitzungen: number }[];
  fehler: { art: string; plattform: string; version: string; name: string; anzahl: number }[];
  absturzberichte: { plattform: string; version: string; name: string; stapel: string; anzahl: number }[];
  /** Zeilen, hinter denen weniger Nutzer stehen, zeigt der Bericht nicht (je Liste gezaehlt). */
  mindestens?: number;
  ausgeblendet?: Record<string, number>;
};

/** W75 (D-287): Anschrift fuer Briefe (Server `Anschrift`), alle Felder frei lassbar. */
export type Anschrift = { vorname: string; nachname: string; strasse: string; plz: string; ort: string; telefon: string; email: string };
export const ANSCHRIFT_FELDER = ["vorname", "nachname", "strasse", "plz", "ort", "telefon", "email"] as const;
export type AnschriftFeld = (typeof ANSCHRIFT_FELDER)[number];

/** W75: an der Person die Anschrift fuer Briefe (null oder fehlend: leer; fehlt bei Servern vor W75). */
export type Person = { id: string; name: string; namensvarianten: string[]; nutzer_id: string | null } & Partial<Record<AnschriftFeld, string | null>>;
export type Kategorie = {
  id: string;
  name: string;
  schluessel: string | null;
  reihenfolge: number;
  system: boolean;
  /** W73c-Server (D-280 e): Zahl der abgelegten Briefe der Kategorie; fehlt bei aelteren Servern */
  anzahl_archiviert?: number;
};
/** email, fax (W15): Kontakt aus dem jüngsten Brief des Absenders; schreibweisen (W47b-2): gelernte Namen, fehlt bis dahin */
/** W82 Web-Empfänger (F3 = B, D-708): `strasse`, `plz`, `ort` und `land` liefert `GET /api/absender` erst nach der Server-Welle
 *  „Anschrift des Absenders“; fehlen sie, kennt der Server keine Anschrift. */
export type Absender = {
  id: string;
  name: string;
  kurzname: string | null;
  email?: string | null;
  fax?: string | null;
  schreibweisen?: string[];
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
  land?: string | null;
};

/** W47b (Plan E6, E7): Absender zusammenlegen, gleich aussehende als Vorschlag. Wie `server/app/schemas/ki.py`. */
export type RegelKurz = { kategorie_id: string; person_id: string | null; bestaetigt: number; aktiv: boolean };
export type DoppelterAbsender = { id: string; name: string; kurzname: string | null; dokumente: number };
/** `verschieden` nur mit `?mit_ausgeblendeten=true` (W47b-2). `beleg` wird nie gezeigt. */
export type DoppelteGruppe = { grund: string; beleg: string; vorschlag_ziel: string; absender: DoppelterAbsender[]; verschieden?: boolean };
/** Antwort von `doppelte` gelesen: heute eine Liste, mit W47b-2 dazu die Zahl der ausgeblendeten Gruppen. */
export type DoppelteAbsender = { gruppen: DoppelteGruppe[]; ausgeblendet: number };
export type ZusammenlegenAus = {
  probe: boolean;
  ziel: Absender;
  quelle: { id: string; name: string; kurzname: string | null };
  dokumente: number;
  entschieden: number;
  vergessen: number;
  akten: { id: string; name: string; dokumente: number }[];
  regel: "keine" | "uebernommen" | "zusammengezaehlt" | "ziel_bleibt";
  regel_quelle: RegelKurz | null;
  regel_ziel: RegelKurz | null;
  schreibweisen: string[];
  felder: string[];
};

export type Dokument = {
  id: string;
  status: Status;
  quelle: string;
  person_id: string | null;
  /** W15: Post an mehrere im Haushalt; traegt keine einzelne Person */
  gemeinsam?: boolean;
  kategorie_id: string | null;
  absender_id: string | null;
  datum: string | null;
  betreff: string | null;
  dokumentart: string | null;
  betrag: string | null;
  seiten: number | null;
  status_grund: string | null;
  eltern_id: string | null;
  /** W7: fortgeschritten signiert, weitere Bearbeitung gesperrt */
  signiert?: boolean;
  erstellt: string;
  geaendert: string;
  /** W37g-Web: null ausser in eingang, verarbeitung, pruefen mit Vorschlag; fehlt bei aelteren Servern und in allen
   *  anderen Antworten (Detail, Suche, Akte, Upload). Fehlen und null heissen fuer die Zeile dasselbe. */
  vorschlag_kurz?: VorschlagKurz | null;
  /** W47e (Server W38-2): Rate am Brief, nur sicher erkannt oder bestaetigt (P4); fehlen bei aelteren Antworten.
   *  Betrag und Turnus gibt es nur zusammen, `rate_ab` nur mit einer Rate. */
  rate_betrag?: string | null;
  rate_turnus?: RateTurnus | null;
  rate_ab?: string | null;
};

/** W47e: Turnus einer Rate am Brief (Server `RateTurnus`, ohne „einmalig“). */
export type RateTurnus = "monatlich" | "vierteljaehrlich" | "halbjaehrlich" | "jaehrlich";

/** W37g-Web: Kurzform des Vorschlags fuer die Posteingangszeile (Server W37f `VorschlagKurz`), nur in Eintraegen von
 *  `GET /api/dokumente`. `datum` ist vom Server geprueft (nur sicher, gueltiger Tag); `absender_konfidenz` und `gesamt`
 *  zeigt die Zeile nicht. */
export type VorschlagKurz = { absender: string | null; absender_konfidenz: number; datum: string | null; gesamt: number };

export type Feld = {
  wert: string | null;
  konfidenz: number;
  beleg: string | null;
  /** W73 (D-278): der Satz um den Beleg aus dem Volltext (Server, hoechstens 200 Zeichen), nur an Stellen mit Beleg;
   *  fehlt bei aelteren Servern und ist null, wenn der Beleg im Text nicht zu finden war. Prueft wird weiter der Beleg. */
  auszug?: string | null;
  grund: string | null;
  id?: string | null;
  kurzname?: string | null;
};

/** W82 Web-8 W8-3 (W88 NW-5, Server W88 Datenmodell B): eine aus „sofort fällig“ abgeleitete Zahlfrist. `hinweis` ist der Text
 *  des Servers („abgeleitet: im Brief steht kein Datum, nur ‚sofort fällig‘“), `basis` und `tage` sagen, wovon aus gezählt ist.
 *  Fehlt bei älteren Servern (dann wie jede Frist). */
export type FristAbgeleitet = { signal: string; basis: "briefdatum" | "eingang"; tage: number; hinweis: string };
export type FristVorschlag = Feld & { art: Fristart; betrag: string | null; abgeleitet?: FristAbgeleitet | null };

export type Vorschlag = {
  quelle: "ki" | "ki+regel" | "regel" | "keine";
  felder: Record<"absender" | "glaeubiger" | "kategorie" | "person" | "datum" | "betreff" | "dokumentart" | "betrag", Feld> & {
    /** W38-3 (Server W38-2): Rate je Zeitraum, fehlt bei aelteren Vorschlaegen */
    rate?: RateFeld;
  };
  kennungen: { wert: string; art: string; beleg: string | null; belegt: boolean }[];
  akte: Feld & {
    neu_art: Aktenart | null;
    neu_name: string | null;
    /** W47c-2: nur bei einer geschlossenen Akte als Ziel. `true` heisst, „Passt“ (`akte_id` gleich `id`) oeffnet sie
     *  wieder; `false`, der Brief kommt hinein und sie bleibt zu. Fehlt bei aelteren Servern und offenen Akten. */
    wieder_oeffnen?: boolean;
  };
  fristen: FristVorschlag[];
  zusammenfassung: string | null;
  handlungsbedarf: { noetig: boolean; text: string | null } | null;
  gesamt: number;
  /** Konfidenz, ab der ein Wert als sicher gilt (sonst nur Vorschlag). */
  schwelle?: number;
  archivieren: boolean;
  gruende: string[];
};

export type DokumentDetail = Dokument & {
  dateien: { art: "original" | "ocr" | "vorschau" | "bearbeitet"; groesse: number; mime: string; dateiname: string | null }[];
  hinweise: Record<string, unknown> | null;
  vorschlag: Vorschlag | null;
  teile: string[];
  /** Welche Fassung der Versand schickt: das Original mit der fremden Signatur oder die aktuelle. */
  versand_fassung?: "original" | "aktuell";
  /** Akten, denen der Brief tatsaechlich zugeordnet ist. */
  akten?: { id: string; name: string; art: Aktenart }[];
  /** W47i: Kennung genau dieses Vorschlags (UUID klein) oder "ohne", nie null; fehlt bei Servern vor W47i. „Passt“ und
   *  „Ablegen“ senden sie unveraendert zurueck, nur wenn sie hier steht (`"vorschlag_id" in detail`). */
  vorschlag_id?: string;
  /** W81 (D-331, Server W81): ob der Brief sich freigeben laesst und seit wann er freigegeben ist; fehlt bei Servern vor
   *  W81 (dann kein Eintrag im Menue). */
  freigabe?: FreigabeStand | null;
};

/** W81: Stand der Freigabe am Brief (`DokumentDetail.freigabe`); `erteilt` ISO-Zeitpunkt oder null. */
export type FreigabeStand = {
  moeglich: boolean;
  erteilt: string | null;
  /** W81-W-2 (F107 A, D-519): nach einem Widerruf fuer diesen Nutzer gesperrt (Brief oder sein Sammelscan), dann gibt es
   *  „Zur Verbesserung freigeben“ in jeder Wahl. Nur die Einzelansicht fuellt es, Listen liefern `false`; Server vor
   *  W81-S-5 liefern es nicht, dann wie `false`. */
  gesperrt?: boolean;
};
/** W81: Vorschau des Servers (`POST /api/dokumente/{id}/freigabe/vorschau`): Einwilligung als Absaetze, Angaben in der
 *  Reihenfolge des Servers, Text mit Platzhaltern; `kennung` geht mit „Freigeben“ unveraendert zurueck. */
export type FreigabeVorschau = {
  fassung: string;
  einwilligung: string[];
  angaben: { name: string; wert: string }[];
  text: string;
  stellen: number;
  kennung: string;
  aufbewahrung_monate: number;
};
/** W81: Antwort von `POST /api/dokumente/{id}/freigabe` (201). */
export type FreigabeAus = { id: string; dokument_id: string; erteilt: string; loeschen_ab: string };
/** W81: Zeile von `GET /api/freigaben`, neueste zuerst; Absender und Briefdatum zur Anzeige aus dem Haushalt. */
export type FreigabeEintrag = { id: string; dokument_id: string; erteilt: string; loeschen_ab: string; absender: string | null; datum: string | null };
/** W81 (Nachtrag D-433): Einwilligungstext ohne Brief fuer die Wahl „alle“ (`GET /api/freigaben/einwilligung`). Die
 *  Fassung geht mit `PUT /api/freigaben/wahl` unveraendert zurueck (der Nachtrag nennt sie dort `int`, 4.2 `JJJJ-MM-TT.n`). */
export type FreigabeEinwilligung = { fassung: string | number; einwilligung: string[] };

/** W47i: Werte der Karte, die der Server zwischen gezeigtem und neuestem Vorschlag vergleicht (`FELDER`), in dieser
 *  Reihenfolge. */
export const GEAENDERT_FELDER = ["absender", "kategorie", "person", "datum", "betrag", "betreff", "dokumentart", "akte", "fristen", "rate"] as const;
export type GeaendertFeld = (typeof GEAENDERT_FELDER)[number];

/** W47i: Koerper des 409 bei „Passt“ oder „Ablegen“ mit einem Vorschlag, der nicht mehr der neueste ist
 *  (`VorschlagGeaendertAus`). `geaendert` null: gezeigter Vorschlag unbekannt, kein Vergleich; [] neu gelesen, gleich. */
export type VorschlagGeaendert = {
  detail: string;
  code: "VORSCHLAG_GEAENDERT";
  vorschlag_id: string;
  vorschlag: Vorschlag | null;
  geaendert: GeaendertFeld[] | null;
};

export type Liste<T> = { eintraege: T[]; gesamt: number; seite: number; pro_seite: number };
export type Treffer = { dokument: Dokument; rang: number; kontext: string };
export type Suchergebnis = Liste<Treffer> & { anfrage: string };

export type Aktenart = "vertrag" | "versicherung" | "forderung" | "sonstige";
export type Fristart = "zahlen" | "widersprechen" | "kuendigen" | "melden" | "sonstige";

export type Turnus = "monatlich" | "vierteljaehrlich" | "halbjaehrlich" | "jaehrlich" | "einmalig";
/** Angaben des Nutzers zu einer Akte (W6); alle Felder freiwillig. */
export type Stammdaten = {
  anbieter?: string | null;
  sparte?: string | null;
  kategorie_id?: string | null;
  person_id?: string | null;
  beitrag?: string | null;
  turnus?: Turnus | null;
  beginn?: string | null;
  ende?: string | null;
  kuendigungsfrist_monate?: number | null;
  verlaengerung_monate?: number | null;
  hauptforderung?: string | null;
  notiz?: string | null;
};
export type BetragAusBrief = {
  betrag: string;
  datum: string;
  dokument_id: string;
  dokumentart: string | null;
  /** W38-3 (Server W38-2): Rate dieses Briefs, falls bekannt */
  rate?: string | null;
  turnus?: RateTurnus | null;
};
/** Aus Angaben und Briefen berechnet (Server). */
export type Berechnet = {
  anbieter: string | null;
  kategorie_id: string | null;
  monatlich: string | null;
  letzter_betrag: BetragAusBrief | null;
  betragsverlauf: BetragAusBrief[];
  kuendigung: { bis: string; zum: string | null; quelle: "brief" | "angaben"; dokument_id: string | null } | null;
  offen: string | null;
  offen_seit: string | null;
  ueberfaellig?: string | null;
  ueberfaellig_seit?: string | null;
  letzter_brief: string | null;
  /** W38-3 (Server W38-2): juengster Brief der Akte mit Rate (auch ohne Betrag), Vorschlag mit „Übernehmen“ */
  rate_vorschlag?: RateAusBrief | null;
  /** W38-3 (Server W38-2): Herkunft des Beitrags, der juengste Brief mit Rate gleich Beitrag und Turnus */
  beitrag_brief?: RateAusBrief | null;
};
export type Akte = {
  id: string;
  art: Aktenart;
  name: string;
  kennungen: string[];
  status: string;
  erstellt: string;
  stammdaten: Stammdaten;
  berechnet: Berechnet;
};
export type Hinweis = { schluessel: string; titel: string; text: string; bezug: string; akte_ids: string[]; dokument_ids: string[] };
export type Uebersicht = {
  stand: string;
  monatlich: string;
  jaehrlich: string;
  kategorien: { kategorie_id: string | null; kategorie: string; monatlich: string; anteil: number; posten: { akte_id: string; name: string; monatlich: string }[] }[];
  unvollstaendig: { akte_id: string; name: string; vorschlag: BetragAusBrief | null; rate?: RateAusBrief | null }[];
  verlauf: { monat: string; summe: string }[];
  hinweise: Hinweis[];
  ausgeblendet: number;
};
export type Frist = {
  id: string;
  dokument_id: string | null;
  akte_id: string | null;
  art: Fristart;
  datum: string;
  betrag: string | null;
  erledigt: string | null;
  /** W80: `brief` aus der Einordnung, `hand` vom Nutzer (ueber Gaist); fehlt bei aelteren Servern und in der Akte */
  herkunft?: string;
  /** W80 (D-352 F1): Titel einer eigenen Frist ohne Brief, bis 120 Zeichen; sonst null oder fehlt */
  titel?: string | null;
  /** W82 Server-5h (BS09) und 5h-S2: mit Brief die Person des Briefs, ohne Brief die Person der Frist (null = gemeinsam);
   *  fehlt bei älteren Servern und in der Akte (`FristBasis`) */
  person_id?: string | null;
  /** mit Brief `Dokument.gemeinsam`; ohne Brief 5h-S immer falsch, 5h-S2 `person_id is null` */
  gemeinsam?: boolean;
};
export type AkteDetail = Akte & { dokumente: Dokument[]; fristen: Frist[] };
/** W82 Web-5c (K15, auftrag-w82-web-5a Punkt 4): Körper von `POST /api/fristen` (Server `FristNeu`), Frist von Hand mit Titel
 *  oder Akte; F5 = A, ohne Brief. */
export type FristNeu = { art: Fristart; datum: string; betrag: string | null; akte_id: string | null; titel: string | null; person_id?: string };
/** W82 Web-5h1 (K45, Server 5h-S und 5h-S2 `FristAendern`): Körper von `PATCH /api/fristen/{id}`, nur geänderte Felder. `betrag`,
 *  `titel` und `akte_id` null entfernen den Wert, `person_id` null heißt gemeinsam (nur Fristen ohne Brief). */
export type FristAendern = Partial<{
  erledigt: boolean;
  art: Fristart;
  datum: string;
  betrag: string | null;
  titel: string | null;
  akte_id: string | null;
  person_id: string | null;
}>;

/** W80 (plan-w80.md 3.4, Server `VorschlagAus`): Vorschlag des Gaist als Karte. Frage, Felder und Texte setzt der Server.
 *  `art` und `zustand` bleiben Text: unbekannte Werte spaeterer Server zeigt die Karte ohne Knoepfe. */
export type GaistVorschlagFeld = { name: string; vorher?: string | null; nachher?: string | null };
export type GaistVorschlagZiel = { typ: string; id: string };
export type GaistVorschlag = {
  id: string;
  art: string;
  zustand: string;
  frage: string;
  felder: GaistVorschlagFeld[];
  ziel: GaistVorschlagZiel | null;
  laeuft_ab: string;
  ergebnis?: { text: string; ziel?: GaistVorschlagZiel | null; offen?: number | null } | null;
  fehler?: string | null;
};
/** W80: geplante, noch nicht gesendete Erinnerung per Mail (`GET /api/erinnerungen`); `text` so sparsam wie die Mail. */
export type Erinnerung = { id: string; am: string; kanal: string; ziel: { typ: string; id: string }; text: string };
export type Aufgabe = {
  id: string;
  titel: string;
  faellig: string | null;
  frist_id: string | null;
  erledigt: string | null;
};

export type Bestaetigung = Partial<{
  absender: string | null;
  person_id: string | null;
  gemeinsam: boolean;
  kategorie_id: string | null;
  datum: string | null;
  betreff: string | null;
  dokumentart: string | null;
  betrag: string | null;
  akte_id: string | null;
  akte_neu: { art: Aktenart; name: string } | null;
  fristen: { art: Fristart; datum: string; betrag: string | null }[];
  /** W38-3 (Server W38-2 `RateEin`): nicht gesendet gilt die sichere Rate des Vorschlags, null heisst ausdruecklich keine */
  rate: { betrag: string; turnus: RateTurnus; ab: string | null } | null;
  /** W47i: Kennung aus dem `GET`, auf dem Karte oder Blatt beruhen; nicht der neueste Vorschlag: 409. Nur senden, wenn
   *  das Detail sie nennt (ein Server vor W47i antwortet sonst 422). */
  vorschlag_id: string;
}>;

/** W47e: neue Akte im PATCH (Server W47a `AkteNeu`), gleichnamige derselben Art nimmt der Server wieder. */
export type AkteNeu = { art: Aktenart; name: string };

/** W47e: `PATCH /api/dokumente/{id}`, nur die geaenderten Felder (Server W47a `DokumentAendern`). `akte_id` und
 *  `akte_neu` schliessen sich aus; beide nur bei abgelegten Briefen. */
export type DokumentAendern = Partial<{
  datum: string | null;
  /** W47e-2 (H7): geleert sendet null, nie "" */
  betreff: string | null;
  kategorie_id: string;
  person_id: string | null;
  gemeinsam: boolean;
  akte_id: string | null;
  akte_neu: AkteNeu;
}>;

// ---------------------------------------------------------------- Dokument-Werkzeuge (W7)

/** Relativ zur sichtbaren Seite (0 bis 1), Ursprung oben links; seite 0-basiert. */
export type Rechteck = { seite: number; x: number; y: number; breite: number; hoehe: number };

export type Werkzeug =
  | { art: "drehen"; drehungen: Record<string, 90 | 180 | 270> }
  | { art: "seiten"; reihenfolge: number[]; drehungen?: Record<string, 90 | 180 | 270> }
  | { art: "ausfuellen"; werte: Record<string, string | boolean> }
  | { art: "stempeln"; texte: (Rechteck & { text: string; groesse?: number })[] }
  | { art: "unterschreiben"; ort: Rechteck }
  // W20b: ohne Passwort am Konto faellt `passwort` weg; dann zaehlt die frische Anmeldung per Link
  | { art: "signieren"; passwort?: string; grund?: string; ort?: Rechteck | null }
  | { art: "schwaerzen"; rechtecke: Rechteck[] }
  | { art: "trennen"; anfaenge: number[] }
  | { art: "zuruecksetzen" };

export type Auftrag = {
  id: string;
  art: string;
  dokument_id: string | null;
  status: "wartet" | "laeuft" | "fertig" | "fehler";
  grund: string | null;
  ergebnis: { dokument_ids?: string[] };
  erstellt: string;
  fertig: string | null;
};

export type Formularfeld = { name: string; art: "text" | "auswahl" | "kontrollkaestchen"; wert: string | null; optionen: string[] };

export type SignaturBefund = {
  feld: string;
  name: string | null;
  zeitpunkt: string | null;
  unveraendert: boolean;
  vertrauenswuerdig: boolean;
  ganzes_dokument: boolean;
};

export type Unterschrift = { art: "gezeichnet" | "getippt"; erstellt: string };

/** W75 (S9): ein offener Platzhalter in Betreff oder Text. `vorbelegt`: Wert zum Bestaetigen (Vertragsende aus der Akte,
 *  „31.12.2027“); `schutz`: Hinweis des KI-Entwurfs, nie ausfuellbar, nur entfernbar. „wahl“ liefert der Server seit
 *  D-304 nicht mehr, die App kennt ihn weiter. */
export type PlatzhalterInfo = {
  name: string;
  beschriftung: string;
  typ: "text" | "datum" | "betrag" | "nummer" | "wahl";
  beispiel?: string | null;
  vorbelegt?: string | null;
  schutz?: boolean;
};

/** W75: `hinweis` steht im Formular ueber dem Text; beide Felder fehlen bei Servern vor W75. */
export type Vorlage = { schluessel: string; titel: string; beschreibung: string; hinweis?: string | null; platzhalter?: PlatzhalterInfo[] };

export type Brief = {
  id: string;
  vorlage: string | null;
  akte_id: string | null;
  bezug_id: string | null;
  absender: string;
  empfaenger: string;
  ort: string;
  datum: string | null;
  betreff: string;
  text: string;
  status: "entwurf" | "fertig";
  dokument_id: string | null;
  /** W15: Kontakt des Empfängers aus seinen Briefen, soweit bekannt */
  empfaenger_email?: string | null;
  empfaenger_fax?: string | null;
  erstellt: string;
  geaendert: string;
  /** W75 (D-287): Absender als Anschrift (ausgeschaltetes Telefon oder Mail fehlt oder ist leer, H9) und Person; fehlen
   *  bei Servern vor W75 und bei Briefen mit freiem Absendertext */
  absender_anschrift?: Partial<Anschrift> | null;
  absender_person_id?: string | null;
  /** W75: das abgelegte PDF hat Platz fuer die Unterschrift (Name am Ende des Texts) */
  unterschrift_platz?: boolean;
  /** W75 (S8): erste Eingabe in Empfaenger, Betreff oder Text; nur `false` heisst unberuehrt */
  beruehrt?: boolean;
  /** W75 (S9): offene Platzhalter aus Betreff und Text in Reihenfolge */
  platzhalter?: PlatzhalterInfo[];
};

export type Versand = {
  id: string;
  dokument_id: string;
  an: string;
  betreff: string;
  status: "wartet" | "gesendet" | "fehler";
  grund: string | null;
  erstellt: string;
  gesendet: string | null;
  /** „absender“ sieht nur, wer verschickt hat (auch der Inhaber nicht). */
  sichtbar_fuer: Sichtbarkeit;
  /** Nur der Absender darf die Sichtbarkeit nachträglich ändern. */
  von_mir: boolean;
};
export type Sichtbarkeit = "haushalt" | "absender";

// ---------------------------------------------------------------- Post per Mail (W8)

export type Eingang = { adresse: string | null; aktiv: boolean; nur_bekannte: boolean; domain: string };

export type MailStatus = "wartet" | "verarbeitet" | "zweifel" | "verworfen";

export type EingangMail = {
  id: string;
  quelle: "adresse" | "imap";
  absender: string;
  betreff: string;
  auszug: string;
  empfangen: string | null;
  erstellt: string;
  groesse: number;
  status: MailStatus;
  grund: string | null;
  /** `hinweis` (W82 Web-10b W10B-2, PA:434): „absender-nicht-bestaetigt“, wenn der Absender bekannt, aber nicht bestätigt ist */
  ergebnis: { dokument_ids?: string[]; doppelt?: number; zweifel_anhaenge?: { name: string; grund: string }[]; hinweis?: string };
  uebernehmbar: boolean;
  /** W13b (D-042): null, solange die Mail wartet; sonst Häkchen (true) oder Fragezeichen (false) */
  bestaetigt: boolean | null;
  bestaetigt_durch: string | null;
};

/** Haushaltsregel aus Bestaetigungen: Post dieses Absenders gehoert in diese Kategorie. */
export type Regel = { id: string; absender_id: string; absender: string; kategorie_id: string; person_id: string | null; bestaetigt: number; aktiv: boolean; geaendert: string };
export type AbsenderRegel = { id: string; adresse: string; art: "erlaubt" | "gesperrt"; erstellt: string };

export type ImapKonto = {
  id: string;
  host: string;
  benutzer: string;
  ordner: string;
  aktiv: boolean;
  zuletzt: string | null;
  fehler: string | null;
  fehlversuche: number;
  erstellt: string;
  /** W82 Web-10b W10B-2: Felder neuer Server (S98-3, S98-5), alle optional; ein alter Server liefert keines */
  anbieter?: string;
  erstabruf_gelesen?: number;
  erstabruf_gesamt?: number | null;
  erstabruf_fertig?: string | null;
  altbriefe_einordnen?: boolean;
  /** zweiter Vertrag des Fortschritts (S96-5, K-7); gelesen wird nur `gelesen`, `fenster_gesamt`, `fertig_am` */
  zeitraum?: string;
  gelesen?: number;
  fenster_gesamt?: number | null;
  fertig_am?: string | null;
};

/** W82 Web-10b W10B-1 (S98-1): Anbieter aus `GET /api/eingang/imap/anbieter`; `host` null heißt „Sonstiger“ (Freitext). */
export type MailAnbieter = {
  schluessel: string;
  name: string;
  host: string | null;
  port?: number;
  verschluesselung?: string;
  hinweis?: string | null;
  link?: string | null;
  domains: string[];
};
export type MailAnbieterListe = { anbieter: MailAnbieter[]; hinweis?: string | null };

// ---------------------------------------------------------------- Produktreife (W9)

export type PlanName = "frei" | "basis" | "plus" | "premium";
export type PlanInfo = {
  name: PlanName;
  titel: string;
  dokumente_monat: number;
  speicher_mb: number;
  ki_monat: number;
  einordnungen_monat: number;
  mitglieder: number;
  postfaecher: number;
  /** W82 Web-10b W10B-3 (S98-4): Briefe, die ein Haushalt zum Start dieses Plans für 30 Tage dazubekommt */
  start_dokumente?: number;
};
export type Nutzung = {
  dokumente_monat: number;
  speicher_bytes: number;
  ki_monat: number;
  einordnungen_monat: number;
  mitglieder: number;
  postfaecher: number;
};
/** W82 Web-10b W10B-3 (S98-4 (6)): gültige Gaben des Startkontingents; die Werte stellt nur der Server */
export type StartGabe = { anlass: "start" | "postfach" | "wechsel"; dokumente: number; verbraucht: number; laeuft_ab: string };
export type Startkontingent = { frei: number; bis: string | null; gaben: StartGabe[] };
/** `bis`, `kaufbar` (W16): der Plan stammt aus einem Kauf und gilt bis dahin; Kaeufe sind eingerichtet. `start` (S98-4) fehlt am alten Server. */
export type PlanStand = { plan: PlanName; plaene: PlanInfo[]; nutzung: Nutzung; testwechsel: boolean; hinweis: string; bis?: string | null; kaufbar?: boolean; start?: Startkontingent };

export type Export = {
  id: string;
  status: "wartet" | "laeuft" | "fertig" | "fehler";
  groesse: number;
  fehler: string | null;
  erstellt: string;
  fertig: string | null;
  laeuft_ab: string | null;
};

export type AuditEintrag = {
  id: number;
  zeit: string;
  aktion: string;
  text: string;
  wer: string;
  ich: boolean;
  dokument_id: string | null;
  dokument_titel: string | null;
  ip: string | null;
};
export type AuditSeite = { eintraege: AuditEintrag[]; weiter: number | null };

export type Mitglied = Nutzer & { ich: boolean };
export type Einladung = { id: string; email: string; rolle: "mitglied"; laeuft_ab: string; erstellt: string };

/** W12b Start-Runde „Kennenlernen“: ein Brief mit dem Satz der KI und ihrem Vorschlag. */
export type KennenlernenKarte = {
  dokument_id: string;
  satz: string;
  kategorie_id: string | null;
  person_id: string | null;
  /** W15: der Vorschlag lautet „euch gemeinsam“ */
  gemeinsam?: boolean;
  konfidenz: number;
};

export type Kennenlernen = {
  aktiv: boolean;
  bestaetigt: number;
  ziel: number;
  karten: KennenlernenKarte[];
};

/** W12b: was der Haushalt fuer einen Absender selbst entschieden hat. */
export type GelerntAbsender = {
  absender_id: string;
  absender: string;
  eintraege: { briefart: string; kategorie_id: string; anzahl: number; umgelegt: number }[];
};

// ---------------------------------------------------------------- Rate (W38-3, Server W38-2)

/** Feld `rate` im Vorschlag (`server/app/ki/pruefung.py`): `wert` ist der Betrag je Zeitraum als Text („21.05“). */
/** W38-5: `ratenplan` true heisst, der Server hat einen vereinbarten Ratenplan erkannt (`plan-w38-5.md` Abschnitt 4); er geht
 *  nie still mit, nur nach „Übernehmen“ im Blatt oder „Ja“ auf der Frage der Karte. Fehlt bei allen anderen Raten. */
export type RateFeld = Feld & { turnus: RateTurnus | null; ab: string | null; ratenplan?: boolean };

/** Rate eines abgelegten Briefs der Akte (`server/app/schemas/finanzen.py` `RateAusBrief`); `datum` ist das Briefdatum,
 *  ohne Briefdatum der Eingang. */
export type RateAusBrief = { betrag: string; turnus: RateTurnus; ab: string | null; datum: string; dokument_id: string };

/** W82 Beta-W: Antwort von `POST /api/rueckmeldungen` (201 neu, 200 dieselbe Kennung desselben Kontos). */
export type RueckmeldungAntwort = { id: string; erstellt: string; bild: boolean };

/** W82 Beta-W: eine Rückmeldung für den Betreiber (`GET /api/betrieb/rueckmeldungen`), ohne Nutzer- und Haushalts-ID, ohne
 *  Namen und Adressen; `haushalt` ist die Beta-Kennung oder null. */
export type RueckmeldungBetrieb = {
  id: string;
  status: "offen" | "erledigt";
  erstellt: string;
  erledigt: string | null;
  plattform: "web" | "ios" | "android";
  build: string;
  geraet: string;
  seite: string;
  fassung: string | null;
  palette: string | null;
  praesenz: string | null;
  text: string;
  bild: boolean;
  bild_groesse: number | null;
  haushalt: string | null;
  /** W82 Beta-W2 (Server Beta-S2, Migration 0046): aus einer Dokumentansicht gesendet, und ob der Brief beim Senden gemeinsam
   *  war; ältere Server ohne die Felder. Nie ein Titel. */
  brief?: boolean;
  gemeinsam?: boolean;
};
